/**
 * Vehicle segments for GPS tracker assets — single source of truth for the
 * segment list (register/edit forms) and the per-segment map marker glyphs.
 *
 * Glyphs are stock Material Design icon paths on a 24x24 viewBox, usable both
 * inside inline-SVG pin markers (Tracking page) and as google.maps Symbol
 * paths (Live Location modal). A null/unknown segment falls back to the
 * generic navigation-arrow marker, so the field is always optional.
 */
export const VEHICLE_TYPES = [
  {value: "TRUCK", label: "Truck / Lorry"},
  {value: "CAR", label: "Car / Jeep"},
  {value: "BIKE", label: "Bike / 2-Wheeler"},
  {value: "BUS", label: "Bus"},
  {value: "VAN", label: "Van / Mini Truck"},
];

export const VEHICLE_GLYPH_PATHS = {
  TRUCK: "M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9 1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z",
  CAR: "M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z",
  BIKE: "M19.44 9.03 15.41 5H11v2h3.59l2 2H5c-2.8 0-5 2.2-5 5s2.2 5 5 5c2.46 0 4.45-1.69 4.9-4h1.65l2.77-2.77c-.21.54-.32 1.14-.32 1.77 0 2.8 2.2 5 5 5s5-2.2 5-5c0-2.65-1.97-4.77-4.56-4.97zM7.82 15C7.4 16.15 6.28 17 5 17c-1.63 0-3-1.37-3-3s1.37-3 3-3c1.28 0 2.4.85 2.82 2H5v2h2.82zM19 17c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z",
  BUS: "M4 16c0 .88.39 1.67 1 2.22V20c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h8v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1.78c.61-.55 1-1.34 1-2.22V6c0-3.5-3.58-4-8-4s-8 .5-8 4v10zm3.5 1c-.83 0-1.5-.67-1.5-1.5S6.67 14 7.5 14s1.5.67 1.5 1.5S8.33 17 7.5 17zm9 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm1.5-6H6V6h12v5z",
  VAN: "M17 5H3c-1.1 0-2 .89-2 2v9h2c0 1.65 1.34 3 3 3s3-1.35 3-3h5.5c0 1.65 1.34 3 3 3s3-1.35 3-3H23v-5l-6-6zM3 11V7h4v4H3zm3 6.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM13 11H9V7h4v4zm4.5 6.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM15 11V7h1l4 4h-5z",
};

/**
 * Top-view single-path silhouettes (48x48 viewBox, nose pointing up) for
 * google.maps Symbol markers — Symbols are monochrome but rotate natively,
 * so these spin with the vehicle's bearing like the generic heading arrow.
 * Anchor at (24,24); subpaths all fill solid.
 */
export const VEHICLE_TOP_PATHS = {
  TRUCK: "M18 2h12a5 5 0 0 1 5 5v8H13V7a5 5 0 0 1 5-5zM12 18h24v25a3 3 0 0 1-3 3H15a3 3 0 0 1-3-3V18z",
  CAR: "M20 2h8a7 7 0 0 1 7 7v29a6 6 0 0 1-6 6H19a6 6 0 0 1-6-6V9a7 7 0 0 1 7-7zM10.5 13H13v4h-2.5v-4zM35 13h2.5v4H35v-4z",
  BIKE: "M22.6 2h2.8a1.4 1.4 0 0 1 1.4 1.4v5.2a1.4 1.4 0 0 1-1.4 1.4h-2.8a1.4 1.4 0 0 1-1.4-1.4V3.4A1.4 1.4 0 0 1 22.6 2zM14 11h20v3H14v-3zM20.5 14h7a3.5 3.5 0 0 1 3.5 3.5v17a5 5 0 0 1-5 5h-4a5 5 0 0 1-5-5v-17a3.5 3.5 0 0 1 3.5-3.5zM21.2 38h5.6v6.6h-5.6V38z",
  BUS: "M19 2h10a6 6 0 0 1 6 6v32a4 4 0 0 1-4 4H17a4 4 0 0 1-4-4V8a6 6 0 0 1 6-6zM10.5 11H13v4h-2.5v-4zM35 11h2.5v4H35v-4z",
  VAN: "M19 2h10a6 6 0 0 1 6 6v34a3 3 0 0 1-3 3H16a3 3 0 0 1-3-3V8a6 6 0 0 1 6-6z",
};

export function vehicleLabel(value) {
  return VEHICLE_TYPES.find((t) => t.value === value)?.label || value || "";
}

/* ------------------------------------------------------------------ */
/* Full-colour, genuinely 3D-rendered marker art — one PNG sprite per  */
/* (vehicle type, compass direction), in public/vehicle-sprites/.      */
/* Superseded a flat/gradient SVG icon: a hand-drawn vector, however   */
/* shaded, still read as a toy next to real 3D-rendered game assets.   */
/*                                                                     */
/* CAR/VAN/TRUCK sprites are Kenney's Car Kit (CC0, kenney.nl)isometric*/
/* renders, used as-is. BIKE/BUS have no equivalent in that pack, so   */
/* they were custom-rendered (Three.js: BIKE from a free MIT-licensed  */
/* motorcycle model, BUS from a simple procedural box mesh) at the     */
/* same camera elevation/lighting so all five sit together as one      */
/* visually consistent fleet — see contact sheet in the PR description.*/
/*                                                                     */
/* Each sprite is a FIXED camera angle (not a rotatable vector), so a  */
/* vehicle's screen heading is shown by picking the nearest of 8       */
/* pre-rendered compass directions rather than rotating one image —    */
/* this is why the live map now SNAPS to 45° steps instead of smoothly */
/* rotating. Mirrors Android's res/drawable-nodpi/ic_vehicle_* set     */
/* exactly (same files) — keep both in lockstep if sprites change.     */
/* ------------------------------------------------------------------ */

const SPRITE_VEHICLE_TYPES = new Set(["TRUCK", "CAR", "BIKE", "BUS", "VAN"]);
const COMPASS_DIRECTIONS = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];

/**
 * Buckets a 0-360 bearing into the nearest of the 8 compass directions our
 * sprite sheets were rendered at (45° steps, N = 0°/up, clockwise).
 */
export function compassDirectionFromBearing(bearingDeg) {
  const norm = ((bearingDeg % 360) + 360) % 360;
  const idx = Math.round(norm / 45) % COMPASS_DIRECTIONS.length;
  return COMPASS_DIRECTIONS[idx];
}

/**
 * URL for a segment's full-colour, pre-rendered marker sprite at the given
 * bearing, or null when the segment has no sprite set (caller falls back to
 * the heading arrow). `bearingDeg` defaults to 0 (the "N"/nose-up sprite).
 */
export function vehicleSpriteUrl(vehicleType, bearingDeg = 0) {
  if (!SPRITE_VEHICLE_TYPES.has(vehicleType)) return null;
  const dir = compassDirectionFromBearing(bearingDeg);
  return `/vehicle-sprites/${vehicleType.toLowerCase()}_${dir}.png`;
}
